import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from 'react';
import log from 'loglevel';

import { useAuth } from '../../../hooks/useAuth';
import {
  streamSupportEvents,
  SupportStreamUnavailableError,
  type SupportStreamFrame,
} from './supportStreamApi';

/** Backoff schedule for reconnects, in ms. The last value repeats. */
const RECONNECT_DELAYS_MS = [1_000, 2_000, 5_000, 10_000, 30_000];
/**
 * Show "Reconnecting…" only after this long without a frame or heartbeat.
 * The server pings every 25 s, so anything shorter would flicker on a healthy,
 * idle stream.
 */
export const STALE_AFTER_MS = 40_000;

export type SupportStreamStatus = 'connecting' | 'live' | 'reconnecting' | 'unavailable';

type Listener = (frame: SupportStreamFrame) => void;

interface SupportStreamContextValue {
  status: SupportStreamStatus;
  /**
   * Bumped whenever the stream comes back without a replay (`ready` with
   * `resumed: false`). Screens refetch what they show when it changes.
   */
  resyncToken: number;
  subscribe: (listener: Listener) => () => void;
}

const SupportStreamContext = createContext<SupportStreamContextValue | null>(null);

/**
 * One agent stream per tab, shared by every Support screen under it (inbox,
 * ticket screen, bell). It remembers the last durable frame id so a reconnect
 * replays what was missed, dedupes on event id (delivery is at-least-once),
 * and closes while the tab is hidden — the `Last-Event-ID` resume collects
 * what landed meanwhile.
 */
export function SupportStreamProvider({
  children,
  tenantId,
}: {
  children: ReactNode;
  /** Follow this tenant instead of the selected one: the sandbox's agent workspace. */
  tenantId?: string;
}): JSX.Element {
  const { getAccessTokenSilently } = useAuth();
  const getTokenRef = useRef(getAccessTokenSilently);
  getTokenRef.current = getAccessTokenSilently;

  const listeners = useRef(new Set<Listener>());
  const [status, setStatus] = useState<SupportStreamStatus>('connecting');
  const [resyncToken, setResyncToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let controller: AbortController | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let staleTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let lastEventId: string | null = null;
    const seen = new Set<string>();

    const markAlive = (): void => {
      if (staleTimer) clearTimeout(staleTimer);
      staleTimer = setTimeout(() => {
        if (!cancelled) setStatus('reconnecting');
      }, STALE_AFTER_MS);
    };

    const dispatch = (frame: SupportStreamFrame): void => {
      for (const listener of [...listeners.current]) {
        try {
          listener(frame);
        } catch (err) {
          log.warn('Support stream listener failed', err);
        }
      }
    };

    const onFrame = (frame: SupportStreamFrame, id: string | null): void => {
      markAlive();
      if (frame.type === 'ready') {
        attempt = 0;
        setStatus('live');
        if (!frame.resumed) setResyncToken((n) => n + 1);
        return;
      }
      if (id) lastEventId = id;
      if (frame.type === 'ticket_event') {
        if (seen.has(frame.eventId)) return;
        seen.add(frame.eventId);
        if (seen.size > 2_000) seen.clear();
      }
      dispatch(frame);
    };

    const clearReconnect = (): void => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectTimer = null;
    };

    const scheduleReconnect = (delayOverride?: number): void => {
      if (cancelled) return;
      clearReconnect();
      const delay =
        delayOverride ?? RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)];
      attempt += 1;
      reconnectTimer = setTimeout(connect, delay);
    };

    function connect(): void {
      clearReconnect();
      if (cancelled || document.hidden || controller) return;
      const attemptController = new AbortController();
      controller = attemptController;
      const settle = (): boolean => {
        if (controller !== attemptController) return false;
        controller = null;
        return true;
      };

      streamSupportEvents({
        getToken: () => getTokenRef.current(),
        tenantId,
        lastEventId,
        onFrame,
        signal: attemptController.signal,
      })
        .then(() => {
          if (!settle()) return;
          setStatus('reconnecting');
          scheduleReconnect();
        })
        .catch((error: unknown) => {
          if (!settle() || cancelled || attemptController.signal.aborted) return;
          if (error instanceof SupportStreamUnavailableError) {
            // Redis is off server-side: keep working without live updates and
            // look again occasionally rather than hammering a 503.
            setStatus('unavailable');
            scheduleReconnect(60_000);
            return;
          }
          log.debug('Support stream dropped; will retry', error);
          setStatus('reconnecting');
          scheduleReconnect();
        });
    }

    const onVisibilityChange = (): void => {
      if (document.hidden) {
        controller?.abort();
        controller = null;
        clearReconnect();
        if (staleTimer) clearTimeout(staleTimer);
        return;
      }
      attempt = 0;
      if (!controller) connect();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    connect();

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearReconnect();
      if (staleTimer) clearTimeout(staleTimer);
      controller?.abort();
      controller = null;
    };
  }, [tenantId]);

  const subscribe = useCallback((listener: Listener) => {
    listeners.current.add(listener);
    return () => {
      listeners.current.delete(listener);
    };
  }, []);

  const value = useMemo(
    () => ({ status, resyncToken, subscribe }),
    [status, resyncToken, subscribe],
  );
  return <SupportStreamContext.Provider value={value}>{children}</SupportStreamContext.Provider>;
}

/** The shared stream's status and resync token. */
export function useSupportStreamStatus(): Pick<
  SupportStreamContextValue,
  'status' | 'resyncToken'
> {
  const ctx = useContext(SupportStreamContext);
  return { status: ctx?.status ?? 'unavailable', resyncToken: ctx?.resyncToken ?? 0 };
}

/**
 * Receive frames from the shared stream. The listener may change identity on
 * every render; only the latest one is called.
 */
export function useSupportStream(listener: Listener): void {
  const ctx = useContext(SupportStreamContext);
  const ref = useRef(listener);
  ref.current = listener;
  useEffect(() => {
    if (!ctx) return undefined;
    return ctx.subscribe((frame) => ref.current(frame));
  }, [ctx]);
}
